/**
 * Marcas de manifiesto pendientes de confirmar contra Django.
 *
 * EL PROBLEMA QUE RESUELVE
 * ------------------------
 * Crear el manifiesto son dos escrituras en dos backends distintos:
 *
 *   1. POST /Manifiest/CreateManifest  (.NET)   → el manifiesto EXISTE
 *   2. POST /shipment-bulk/{id}/manifest (Django) → la carga queda marcada
 *
 * No hay transacción entre las dos. Si la 2 falla, el manifiesto ya se creó pero
 * la carga sigue sin vínculo: el botón "Crear Manifiesto"
 * vuelve a aparecer y quien lo apriete genera un SEGUNDO manifiesto para las
 * mismas guías. Y como el fallo suele ser de red o transitorio, perder ese dato
 * al cerrar la pestaña deja el problema sin dueño.
 *
 * Por eso el pendiente se persiste: sobrevive al reload y al cierre del
 * navegador, y habilita un reintento explícito.
 *
 * POR QUÉ ES SEGURO REINTENTAR
 * ----------------------------
 * `POST /shipment-bulk/{id}/manifest` ES IDEMPOTENTE: llamarlo de nuevo refresca
 * la fecha y el vínculo, y no exige un estado particular de la carga. Así que
 * reintentar no puede duplicar nada.
 *
 * Además, el pendiente solo existe cuando el vínculo FALLÓ, y al abrir el detalle
 * se contrasta contra el servidor: si `manifest_id` ya no es null —porque alguien
 * lo vinculó desde otra pestaña, o el backend se arregló— el pendiente se
 * descarta sin preguntar.
 *
 * Todo va envuelto en try/catch: `localStorage` tira en modo privado, con la
 * cuota llena, o si el navegador lo bloquea. Que no se pueda guardar el
 * recordatorio no puede romper la pantalla.
 */

const STORAGE_KEY = 'bulk-upload-pending-manifest'

export interface PendingManifestMark {
  /** Nº del manifiesto ya creado en .NET (ej. "MIA603"). */
  code: string
  /**
   * GUID del manifiesto. OBLIGATORIO: el endpoint de vínculo recibe
   * `manifest_id` y sin él no hay nada que reintentar. Si .NET no lo devolviera,
   * no se crea pendiente — un botón de reintento que no puede funcionar es peor
   * que ninguno.
   */
  id: string
  /** Cuándo falló la marca (ISO). */
  at: string
  /** Último error, para poder mostrarlo tras un reload. */
  lastError: string
}

type Store = Record<string, PendingManifestMark>

const readStore = (): Store => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Store) : {}
  } catch {
    return {}
  }
}

const writeStore = (store: Store): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch {
    // Sin persistencia el reintento sigue disponible en esta sesión; solo no
    // sobrevive al reload. No es motivo para romper nada.
  }
}

/** Marca pendiente de esta carga, o null. */
export function getPendingManifestMark(
  headerId?: string | null
): PendingManifestMark | null {
  if (!headerId) return null
  const entry = readStore()[headerId]
  // `id` se valida además de `code`: entradas viejas guardadas con id null no
  // sirven para reintentar, así que se descartan.
  return entry && typeof entry.code === 'string' && typeof entry.id === 'string'
    ? entry
    : null
}

export function savePendingManifestMark(
  headerId: string,
  mark: Omit<PendingManifestMark, 'at'> & { at?: string }
): void {
  if (!headerId) return
  const store = readStore()
  store[headerId] = {
    code: mark.code,
    id: mark.id,
    lastError: mark.lastError,
    at: mark.at ?? new Date().toISOString(),
  }
  writeStore(store)
}

export function clearPendingManifestMark(headerId?: string | null): void {
  if (!headerId) return
  const store = readStore()
  if (!(headerId in store)) return
  delete store[headerId]
  writeStore(store)
}
